import { Fragment, useState } from "react";
import { useViewer } from "~/providers/viewer";
import { DangerZone, DangerZoneItem } from "~/components/danger-zone";
import {
  useDeleteAssetsMutation,
  useDeleteWorkspacesMutation,
} from "~/operations";
import { useCanDeleteSpace } from "~/lib/iam/actions/mondoo/captain/Captain/DeleteSpace";
import { useCanDeleteAssets } from "~/lib/iam/actions/mondoo/policy/AssetStore/DeleteAssets";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { SpaceNameSettings } from "~/pages/space-settings/name-and-description-settings";
import { AssetSelectionSettings } from "~/pages/space-settings/asset-selection-settings";
import { useScope } from "~/hooks/useScope";
import { AssetSettings } from "./AssetSettings";
import { ExceptionsSettings } from "./ExceptionsSettings";

type SpaceSettingsState = {
  isDeleting: boolean;
  isDeletingAssets: boolean;
};

export type GeneralSpaceSettingsProps = {};

export function GeneralSpaceSettings({}: GeneralSpaceSettingsProps) {
  const navigate = useNavigate();
  const { organizationScope, spaceScope, workspaceScope, activeScope } =
    useScope();
  const { enqueueSnackbar } = useSnackbar();
  const { deleteSpace } = useViewer();
  const [deleteAssets] = useDeleteAssetsMutation();
  const [deleteWorkspaces] = useDeleteWorkspacesMutation();

  const isSpaceScope = spaceScope && activeScope?.type === spaceScope.type;
  const isWorkspaceScope =
    workspaceScope && activeScope?.type === workspaceScope.type;

  const [state, setState] = useState<SpaceSettingsState>({
    isDeleting: false,
    isDeletingAssets: false,
  });

  const mergeState = (params: Partial<SpaceSettingsState>) => {
    return setState((prevState) => ({
      ...prevState,
      ...params,
    }));
  };

  const handleDeleteAllAssets = async (): Promise<void> => {
    mergeState({ isDeletingAssets: true });
    try {
      await deleteAssets({
        variables: {
          input: { spaceMrn: spaceScope?.mrn || "", assetMrns: [] },
        },
      });
      enqueueSnackbar(`Successfully deleted all assets within space`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(`Failed to delete all assets within space`, {
        variant: "error",
      });
    } finally {
      mergeState({ isDeletingAssets: false });
    }
  };

  const handleDeleteSpace = async (): Promise<void> => {
    mergeState({ isDeleting: true });
    try {
      await deleteSpace({ variables: { spaceMrn: spaceScope?.mrn || "" } });
      enqueueSnackbar(`Successfully deleted space`, { variant: "success" });
      navigate(`/organization/spaces?${organizationScope?.params}`);
    } catch (error) {
      mergeState({ isDeleting: false });
      enqueueSnackbar(`Failed to delete space: ${spaceScope?.name}`, {
        variant: "error",
      });
    }
  };

  const handleDeleteWorkspace = async (): Promise<void> => {
    mergeState({ isDeleting: true });
    try {
      const mrns = workspaceScope ? [workspaceScope.mrn] : [];
      await deleteWorkspaces({ variables: { input: { mrns } } });
      enqueueSnackbar(`Successfully deleted workspace`, { variant: "success" });
      navigate(`/space/workspaces?${spaceScope?.params}`);
    } catch (error) {
      mergeState({ isDeleting: false });
      enqueueSnackbar(`Failed to delete workspace: ${workspaceScope?.name}`, {
        variant: "error",
      });
    }
  };

  const deleteSpacePermission = useCanDeleteSpace(spaceScope);
  const deleteAssetsPermission = useCanDeleteAssets(spaceScope);

  return (
    <Fragment>
      {isWorkspaceScope && <AssetSelectionSettings />}

      <SpaceNameSettings />

      {isSpaceScope && <AssetSettings space={spaceScope} />}
      {isSpaceScope && <ExceptionsSettings space={spaceScope} />}

      {((deleteAssetsPermission && !workspaceScope) ||
        deleteSpacePermission) && (
        <DangerZone>
          {!workspaceScope && deleteAssetsPermission && (
            <DangerZoneItem
              primary="Delete all assets"
              secondary="Deleting assets permanently erases all the associated data, including reports."
              confirm="I confirm that I want to delete all the assets in this space and all their related information."
              action="Delete"
              onClick={handleDeleteAllAssets}
              isDeleting={state.isDeletingAssets}
            />
          )}
          {!workspaceScope && deleteSpacePermission && (
            <DangerZoneItem
              primary="Delete space"
              secondary="Deleting a space permanently erases all the associated data, including assets and reports."
              confirm="I confirm that I want to delete this space, including all its related content."
              action="Delete"
              onClick={handleDeleteSpace}
              isDeleting={state.isDeleting}
            />
          )}
          {workspaceScope && deleteSpacePermission && (
            <DangerZoneItem
              primary="Delete workspace"
              secondary="Deleting this workspace will erase it from this space, for everyone. Anything in your space, such as your assets or policies, will not be affected. This action cannot be undone."
              confirm="I confirm that I want to delete this workspace."
              action="Delete"
              onClick={handleDeleteWorkspace}
              isDeleting={state.isDeleting}
            />
          )}
        </DangerZone>
      )}
    </Fragment>
  );
}
